import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { UserInfo, WeComOAuthResponse, FeishuOAuthResponse } from '@/api/authApi';
import * as authApi from '@/api/authApi';
import * as permissionApi from '@/api/permissionApi';
import { resetClientSessionState } from '@/utils/userSessionReset';
import { setUserSessionScope } from '@/utils/userSessionScope';

/**
 * Token存储键名
 */
const ACCESS_TOKEN_KEY = 'aipd_access_token';
const REFRESH_TOKEN_KEY = 'aipd_refresh_token';
const USER_INFO_KEY = 'aipd_user_info';
/**
 * 勾选「自动登录」时写入：自「最近一次顺延」起连续空闲超过该时长后，前端清除本地登录态（默认 2 小时）。
 * 与登录时刻无关；用户保持操作（已认证 API 成功）时会顺延，见 touchRememberSessionExpiry。
 */
const AUTH_EXPIRES_AT_KEY = 'aipd_auth_expires_at';

const REMEMBER_SESSION_MS = 2 * 60 * 60 * 1000;
/** 避免每个请求写 localStorage；至少间隔该时间才再次顺延 */
const REMEMBER_SESSION_TOUCH_MIN_MS = 60 * 1000;

let lastRememberSessionTouchMs = 0;

export type AuthPersistMode = 'local' | 'session';

function clearStorageKeys(storage: Storage) {
  storage.removeItem(ACCESS_TOKEN_KEY);
  storage.removeItem(REFRESH_TOKEN_KEY);
  storage.removeItem(USER_INFO_KEY);
}

function clearAllTokenStorages() {
  clearStorageKeys(localStorage);
  clearStorageKeys(sessionStorage);
  try {
    localStorage.removeItem(AUTH_EXPIRES_AT_KEY);
  } catch {
    /* ignore */
  }
}

function readTokenPair(): {
  access: string | null;
  refresh: string | null;
  mode: AuthPersistMode | null;
} {
  try {
    const localAccess = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (localAccess) {
      const expRaw = localStorage.getItem(AUTH_EXPIRES_AT_KEY);
      if (expRaw) {
        const exp = parseInt(expRaw, 10);
        if (!Number.isNaN(exp) && Date.now() > exp) {
          clearAllTokenStorages();
          return { access: null, refresh: null, mode: null };
        }
      }
      return {
        access: localAccess,
        refresh: localStorage.getItem(REFRESH_TOKEN_KEY),
        mode: 'local',
      };
    }
    const sessAccess = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    if (sessAccess) {
      return {
        access: sessAccess,
        refresh: sessionStorage.getItem(REFRESH_TOKEN_KEY),
        mode: 'session',
      };
    }
  } catch (e) {
    console.error('读取 Token 失败:', e);
  }
  return { access: null, refresh: null, mode: null };
}

function readUserInfoFromStorage(mode: AuthPersistMode): UserInfo | null {
  try {
    const storage = mode === 'session' ? sessionStorage : localStorage;
    const stored = storage.getItem(USER_INFO_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    console.error('加载用户信息失败:', e);
    return null;
  }
}

/**
 * 认证状态管理Store
 */
export const useAuthStore = defineStore('auth', () => {
  const initial = readTokenPair();
  const tokenPersistMode = ref<AuthPersistMode | null>(initial.mode);

  // 状态
  const accessToken = ref<string | null>(initial.access);
  const refreshToken = ref<string | null>(initial.refresh);
  const userInfo = ref<UserInfo | null>(
    initial.mode ? readUserInfoFromStorage(initial.mode) : null,
  );
  if (userInfo.value?.userId) {
    setUserSessionScope(userInfo.value.tenantId, userInfo.value.userId);
  }
  const roles = ref<Set<string>>(new Set());
  const loading = ref(false);
  const error = ref<string | null>(null);
  /** 退出登录过程中：抑制并发 401 弹错 */
  const loggingOut = ref(false);

  // 计算属性
  const isAuthenticated = computed(() => {
    return !!accessToken.value && !!userInfo.value;
  });

  const currentUser = computed(() => userInfo.value);

  /** 用户角色列表（用于权限判断与展示） */
  const roleList = computed(() => Array.from(roles.value));

  function getActiveStorage(): Storage {
    return tokenPersistMode.value === 'session' ? sessionStorage : localStorage;
  }

  function persistTokens(access: string | null, refresh: string | null): void {
    if (!tokenPersistMode.value) return;
    try {
      const st = getActiveStorage();
      if (access) st.setItem(ACCESS_TOKEN_KEY, access);
      else st.removeItem(ACCESS_TOKEN_KEY);
      if (refresh) st.setItem(REFRESH_TOKEN_KEY, refresh);
      else st.removeItem(REFRESH_TOKEN_KEY);
    } catch (e) {
      console.error('保存Token失败:', e);
    }
  }

  function persistUserInfo(info: UserInfo | null): void {
    if (!tokenPersistMode.value) return;
    try {
      const st = getActiveStorage();
      if (info) st.setItem(USER_INFO_KEY, JSON.stringify(info));
      else st.removeItem(USER_INFO_KEY);
    } catch (e) {
      console.error('保存用户信息失败:', e);
    }
  }

  /**
   * 设置认证信息
   * 飞书登录时，后端已在回调中完成 assignRoleByDepartment，userInfo.roles 即最新角色
   * @param remember 勾选「自动登录」时为 true（默认）：localStorage + 2 小时前端失效；false 为仅当前标签页 sessionStorage
   */
  function setAuth(
    response: WeComOAuthResponse | FeishuOAuthResponse,
    options?: { remember?: boolean },
  ): void {
    const remember = options?.remember !== false;

    clearAllTokenStorages();
    lastRememberSessionTouchMs = 0;
    tokenPersistMode.value = remember ? 'local' : 'session';

    accessToken.value = response.accessToken;
    refreshToken.value = response.refreshToken;
    userInfo.value = response.userInfo as unknown as UserInfo;

    persistTokens(response.accessToken, response.refreshToken);
    persistUserInfo(userInfo.value);

    resetClientSessionState(userInfo.value?.tenantId, userInfo.value?.userId);

    try {
      if (remember) {
        const now = Date.now();
        localStorage.setItem(AUTH_EXPIRES_AT_KEY, String(now + REMEMBER_SESSION_MS));
        lastRememberSessionTouchMs = now;
      }
    } catch (e) {
      console.error('写入登录过期时间失败:', e);
    }

    error.value = null;
    // 飞书 / 企微 OAuth 响应已含 roles 时直接使用，避免额外请求
    const oauthUserInfo = response.userInfo as { roles?: string[] } | undefined;
    if (oauthUserInfo?.roles && Array.isArray(oauthUserInfo.roles)) {
      setRoles(oauthUserInfo.roles);
    } else {
      fetchUserRoles();
    }
  }

  /**
   * 设置用户角色（供 permissionStore 同步使用）
   */
  function setRoles(roleList: string[]): void {
    roles.value = new Set(roleList ?? []);
  }

  /**
   * 更新用户信息（如从飞书同步后刷新头像、姓名等）
   */
  function updateUserInfo(partial: Partial<UserInfo>): void {
    if (!userInfo.value) return;
    userInfo.value = { ...userInfo.value, ...partial };
    persistUserInfo(userInfo.value);
  }

  /**
   * 从后端获取当前用户角色列表
   */
  async function fetchUserRoles(): Promise<void> {
    if (!accessToken.value) return;
    try {
      const response = await permissionApi.getUserPermissions();
      roles.value = new Set(response.roles ?? []);
    } catch (err) {
      console.error('获取用户角色失败:', err);
      roles.value = new Set();
    }
  }

  /**
   * 清除认证信息
   */
  function clearAuth(): void {
    accessToken.value = null;
    refreshToken.value = null;
    userInfo.value = null;
    roles.value = new Set();
    tokenPersistMode.value = null;
    lastRememberSessionTouchMs = 0;

    clearAllTokenStorages();

    resetClientSessionState();

    error.value = null;
  }

  /**
   * 企业微信登录
   */
  async function weComLogin(corpId?: string, redirectUri?: string): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const authUrl = await authApi.getWeComLoginUrl(corpId, redirectUri);
      // 跳转到企业微信登录页面
      window.location.href = authUrl;
    } catch (err: any) {
      error.value = err.message || '登录失败';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * 处理登录回调
   */
  async function handleLoginCallback(
    code: string,
    state: string,
    corpId?: string
  ): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const response = await authApi.handleWeComCallback(code, state, corpId);
      setAuth(response, { remember: true });
    } catch (err: any) {
      error.value = err.message || '登录回调处理失败';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * 处理飞书登录回调（桌面端免登录/应用入口回调）
   */
  async function handleFeishuCallback(code: string, state: string): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const response = await authApi.handleFeishuCallback(code, state);
      setAuth(response, { remember: true });
    } catch (err: any) {
      error.value = err?.message || '飞书登录回调处理失败';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * 获取用户信息
   */
  async function fetchUserInfo(): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const info = await authApi.getUserInfo();
      userInfo.value = info;
      persistUserInfo(info);
    } catch (err: any) {
      error.value = err.message || '获取用户信息失败';
      // 如果获取失败，可能是Token过期，清除认证信息
      if (err.status === 401) {
        clearAuth();
      }
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * 刷新Token
   */
  async function refreshAccessToken(): Promise<boolean> {
    if (!refreshToken.value) {
      return false;
    }

    try {
      const response = await authApi.refreshToken(refreshToken.value);
      accessToken.value = response.accessToken;
      refreshToken.value = response.refreshToken;

      persistTokens(response.accessToken, response.refreshToken);
      if (tokenPersistMode.value === 'local') {
        try {
          const now = Date.now();
          localStorage.setItem(AUTH_EXPIRES_AT_KEY, String(now + REMEMBER_SESSION_MS));
          lastRememberSessionTouchMs = now;
        } catch {
          /* ignore */
        }
      }

      return true;
    } catch (err) {
      console.error('刷新Token失败:', err);
      clearAuth();
      return false;
    }
  }

  /**
   * 退出登录
   */
  async function logout(): Promise<void> {
    loading.value = true;
    error.value = null;
    loggingOut.value = true;

    try {
      await authApi.logout();
    } catch (err: any) {
      console.error('退出登录失败:', err);
    } finally {
      clearAuth();
      loading.value = false;
    }
  }

  function endLoggingOut(): void {
    loggingOut.value = false;
  }

  /**
   * 更新Token（用于自动续期）
   */
  function updateAccessToken(token: string): void {
    accessToken.value = token;
    persistTokens(token, refreshToken.value);
    touchRememberSessionExpiry();
  }

  /**
   * 自动登录（localStorage）下滑动续期：有访问令牌时把前端空闲截止时间推迟到「现在 + 2h」。
   * 应在用户仍处于已登录状态下的成功 API 回调中调用（已做节流）。
   */
  function touchRememberSessionExpiry(): void {
    if (tokenPersistMode.value !== 'local') return;
    try {
      if (!localStorage.getItem(ACCESS_TOKEN_KEY)) return;
      const now = Date.now();
      if (now - lastRememberSessionTouchMs < REMEMBER_SESSION_TOUCH_MIN_MS) return;
      lastRememberSessionTouchMs = now;
      localStorage.setItem(AUTH_EXPIRES_AT_KEY, String(now + REMEMBER_SESSION_MS));
    } catch {
      /* ignore */
    }
  }

  return {
    // 状态
    accessToken,
    refreshToken,
    userInfo,
    roles: roleList,
    loading,
    error,
    loggingOut,
    // 计算属性
    isAuthenticated,
    currentUser,
    // 方法
    setAuth,
    setRoles,
    updateUserInfo,
    fetchUserRoles,
    weComLogin,
    handleLoginCallback,
    handleFeishuCallback,
    fetchUserInfo,
    refreshAccessToken,
    logout,
    endLoggingOut,
    clearAuth,
    updateAccessToken,
    touchRememberSessionExpiry,
  };
});
